import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

// Two UI surfaces show task state from manual fetches that live OUTSIDE RTK
// Query, so the Tasks / DashboardStats tag invalidations on the mutations
// below can't refresh them:
//   1. Layout.jsx — the sidebar "Tasks" badge (/notifications/sidebar-counts,
//      60s poll, component state)
//   2. NotificationCenter.jsx — the bell's synthetic task_due / task_overdue
//      rows (/notifications, 30s poll, component state)
// Both would otherwise stay stale after completing a task until their next
// poll or a full page reload. Broadcast a window event after any task
// mutation succeeds so both can re-pull immediately — clearing the badge and
// the bell row the moment a task is taken care of.
const broadcastTasksChanged = async (_arg, { queryFulfilled }) => {
  try {
    await queryFulfilled;
    window.dispatchEvent(new Event("stacked:tasks-changed"));
  } catch { /* mutation failed — nothing changed, nothing to refresh */ }
};

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: 30, // Refetch if data is older than 30 seconds
  tagTypes: ["Dashboard", "DashboardStats", "Tasks", "Messages"],
  endpoints: (build) => ({
    // Get comprehensive dashboard stats
    getDashboardStats: build.query({
      query: () => ({
        url: "/dashboard/stats",
        method: "GET",
      }),
      providesTags: ["DashboardStats"],
      // Transform response to ensure consistent structure
      transformResponse: (response) => ({
        totalClients: response.totalClients || 0,
        activeListings: response.activeListings || 0,
        messagesSent: response.messagesSent || 0,
        tasksDue: response.tasksDue || 0,
        totalProperties: response.totalProperties || 0,
        closedDeals: response.closedDeals || 0,
        pendingTasks: response.pendingTasks || 0,
        underContract: response.underContract || 0,
        upcomingAppointments: response.upcomingAppointments || 0,
        // Change percentages
        clientsChange: response.clientsChange || 0,
        listingsChange: response.listingsChange || 0,
        messagesChange: response.messagesChange || 0,
        // Monthly data for charts
        monthlyData: response.monthlyData || [],
      }),
    }),

    // Get tasks for the user - uses task-management endpoint
    getTasks: build.query({
      query: ({ status, dueDate, page = 1, limit = 50 } = {}) => ({
        url: "/task-management",
        method: "GET",
        params: { status, dueDate, page, limit },
      }),
      providesTags: ["Tasks"],
      transformResponse: (response) => ({
        tasks: response.tasks || response.data || [],
        total: response.total || 0,
        dueTodayCount: response.dueTodayCount || 0,
        overdueCount: response.overdueCount || 0,
      }),
    }),

    // Create a task
    createTask: build.mutation({
      query: (task) => ({
        url: "/task-management",
        method: "POST",
        body: task,
      }),
      invalidatesTags: ["Tasks", "DashboardStats"],
      onQueryStarted: broadcastTasksChanged,
    }),

    // Update a task
    updateTask: build.mutation({
      query: ({ id, ...task }) => ({
        url: `/task-management/${id}`,
        method: "PUT",
        body: task,
      }),
      invalidatesTags: ["Tasks", "DashboardStats"],
      onQueryStarted: broadcastTasksChanged,
    }),

    // Delete a task
    deleteTask: build.mutation({
      query: (id) => ({
        url: `/task-management/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks", "DashboardStats"],
      onQueryStarted: broadcastTasksChanged,
    }),

    // Mark task as complete
    completeTask: build.mutation({
      query: (id) => ({
        url: `/task-management/${id}/complete`,
        method: "PATCH",
      }),
      invalidatesTags: ["Tasks", "DashboardStats"],
      onQueryStarted: broadcastTasksChanged,
    }),

    // Get message stats
    getMessageStats: build.query({
      query: ({ period = "30d" } = {}) => ({
        url: "/messages/stats",
        method: "GET",
        params: { period },
      }),
      providesTags: ["Messages"],
      transformResponse: (response) => ({
        totalSent: response.totalSent || 0,
        totalReceived: response.totalReceived || 0,
        unread: response.unread || 0,
        thisWeek: response.thisWeek || 0,
        thisMonth: response.thisMonth || 0,
      }),
    }),

    // Get growth data for charts
    getGrowthData: build.query({
      query: ({ period = "6m" } = {}) => ({
        url: "/dashboard/growth",
        method: "GET",
        params: { period },
      }),
      providesTags: ["Dashboard"],
      transformResponse: (response) => ({
        monthlyData: response.monthlyData || [],
        clientGrowth: response.clientGrowth || 0,
        propertyGrowth: response.propertyGrowth || 0,
        revenueGrowth: response.revenueGrowth || 0,
      }),
    }),

    // Log activity
    logActivity: build.mutation({
      query: (activity) => ({
        url: "/activities",
        method: "POST",
        body: activity,
      }),
      invalidatesTags: ["Dashboard"],
    }),
  }),
});

export const {
  useGetDashboardStatsQuery,
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useCompleteTaskMutation,
  useGetMessageStatsQuery,
  useGetGrowthDataQuery,
  useLogActivityMutation,
} = dashboardApi;
